import React, { useCallback, useEffect, useState } from 'react';
import api from '../../api/api';
import { SupplierPurchaseOrder } from '../../types';

type ReceiveRow = {
  quantity: string;
  unitPrice: string;
  lotNumber: string;
  manufactureDate: string;
  expirationDate: string;
};

const statusLabels: Record<string, string> = {
  Draft: 'Чернетка',
  Submitted: 'Відправлено',
  PartiallyReceived: 'Частково отримано',
  Received: 'Отримано',
  Cancelled: 'Скасовано',
};
const th: React.CSSProperties = { padding: 9, textAlign: 'left', background: 'var(--crm-surface-alt)', color: 'var(--crm-text)', borderBottom: '2px solid var(--crm-border)' };
const td: React.CSSProperties = { padding: 8, borderBottom: '1px solid var(--crm-table-row-border)', verticalAlign: 'top' };
type ButtonTone = 'success' | 'info' | 'danger' | 'warning' | 'muted';
const button = (tone: ButtonTone, extra: React.CSSProperties = {}): React.CSSProperties => ({
  background: tone === 'muted' ? 'var(--crm-surface-alt)' : `var(--crm-${tone}-bg)`,
  color: tone === 'muted' ? 'var(--crm-text-muted)' : `var(--crm-${tone})`,
  border: `1px solid var(--crm-${tone === 'muted' ? 'border' : tone})`,
  borderRadius: 'var(--crm-radius-md)',
  padding: '7px 12px',
  cursor: 'pointer',
  ...extra,
});
const fieldStyle: React.CSSProperties = {
  color: 'var(--crm-text)',
  background: 'var(--crm-input-bg)',
  border: '1px solid var(--crm-border)',
  borderRadius: 'var(--crm-radius-md)',
  padding: 6,
};

const SupplierPurchaseOrdersPage: React.FC = () => {
  const [orders, setOrders] = useState<SupplierPurchaseOrder[]>([]);
  const [selected, setSelected] = useState<SupplierPurchaseOrder | null>(null);
  const [receiveRows, setReceiveRows] = useState<Record<number, ReceiveRow>>({});
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [receiptDate, setReceiptDate] = useState(new Date().toISOString().slice(0, 10));
  const [paymentDueDate, setPaymentDueDate] = useState(new Date().toISOString().slice(0, 10));
  const [notes, setNotes] = useState('');
  const [isPaid, setIsPaid] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const overdueDays = (order: SupplierPurchaseOrder) => {
    if (!order.expectedDeliveryDate || !['Submitted', 'PartiallyReceived'].includes(order.status)) return 0;
    const due = new Date(order.expectedDeliveryDate);
    due.setHours(0, 0, 0, 0);
    return Math.max(0, Math.floor((today.getTime() - due.getTime()) / 86400000));
  };

  const load = useCallback(async (selectedId?: number) => {
    const response = await api.get<SupplierPurchaseOrder[]>('/supplierpurchaseorders');
    setOrders(response.data);
    if (selectedId) setSelected(response.data.find(item => item.id === selectedId) ?? null);
  }, []);

  useEffect(() => { load().catch(() => setError('Не вдалося завантажити замовлення.')); }, [load]);

  const open = (order: SupplierPurchaseOrder) => {
    setSelected(order);
    setError('');
    const rows: Record<number, ReceiveRow> = {};
    order.items.forEach(item => {
      rows[item.id] = {
        quantity: item.remainingQuantity > 0 ? String(item.remainingQuantity) : '0',
        unitPrice: String(item.unitPrice),
        lotNumber: '',
        manufactureDate: '',
        expirationDate: '',
      };
    });
    setReceiveRows(rows);
    const due = new Date(`${receiptDate}T12:00:00`);
    due.setDate(due.getDate() + (order.supplierPaymentTermDays || 0));
    setPaymentDueDate(due.toISOString().slice(0, 10));
  };

  const changeStatus = async (status: 'Submitted' | 'Cancelled') => {
    if (!selected) return;
    if (!window.confirm(`Змінити статус замовлення на «${statusLabels[status]}»?`)) return;
    setBusy(true);
    setError('');
    try {
      const response = await api.post<SupplierPurchaseOrder>(
        `/supplierpurchaseorders/${selected.id}/status`,
        { status },
        { headers: { 'If-Match': `"${selected.version}"` } },
      );
      setSelected(response.data);
      await load(response.data.id);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Не вдалося змінити статус.');
    } finally {
      setBusy(false);
    }
  };

  const receive = async () => {
    if (!selected) return;
    const items = selected.items
      .map(item => {
        const row = receiveRows[item.id];
        return {
          orderItemId: item.id,
          quantity: Number((row?.quantity || '0').replace(',', '.')),
          unitPrice: Number((row?.unitPrice || String(item.unitPrice)).replace(',', '.')),
          lotNumber: row?.lotNumber || '',
          manufactureDate: row?.manufactureDate || null,
          expirationDate: row?.expirationDate || null,
        };
      })
      .filter(item => item.quantity > 0);
    if (items.length === 0) {
      setError('Вкажіть кількість хоча б для однієї позиції.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const response = await api.post<{ receiptId: number; order: SupplierPurchaseOrder }>(
        `/supplierpurchaseorders/${selected.id}/receive`,
        { date: receiptDate, paymentDueDate, invoiceNumber, notes, isPaid, items },
        {
          headers: {
            'If-Match': `"${selected.version}"`,
            'Idempotency-Key': `supplier-receipt-${selected.id}-${Date.now()}`,
          },
        },
      );
      setSelected(response.data.order);
      setInvoiceNumber('');
      setNotes('');
      open(response.data.order);
      await load(response.data.order.id);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.response?.data?.title || 'Не вдалося провести приймання.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="supplier-purchase-orders-page" style={{ padding: 24, maxWidth: 1450, margin: '0 auto' }}>
      <h2 style={{ color: 'var(--crm-text)' }}>Замовлення постачальникам</h2>
      {orders.length > 0 && !selected && (
        <div style={{ display: 'flex', gap: 12, marginBottom: 14, flexWrap: 'wrap' }}>
          <div style={{ background: 'var(--crm-info-bg)', border: '1px solid var(--crm-info)', borderRadius: 7, padding: '10px 14px', color: 'var(--crm-info)' }}>Відкриті: <strong>{orders.filter(order => ['Submitted', 'PartiallyReceived'].includes(order.status)).length}</strong></div>
          <div style={{ background: 'var(--crm-danger-bg)', border: '1px solid var(--crm-danger)', borderRadius: 7, padding: '10px 14px', color: 'var(--crm-danger)' }}>Прострочені: <strong>{orders.filter(order => overdueDays(order) > 0).length}</strong></div>
          <div style={{ background: 'var(--crm-warning-bg)', border: '1px solid var(--crm-warning)', borderRadius: 7, padding: '10px 14px', color: 'var(--crm-text)' }}>Фактичне відхилення ціни: <strong>{orders.reduce((sum, order) => sum + order.priceVariancePercent, 0) / orders.length >= 0 ? '+' : ''}{(orders.reduce((sum, order) => sum + order.priceVariancePercent, 0) / orders.length).toFixed(1)}%</strong></div>
        </div>
      )}
      {error && <div role="alert" style={{ background: 'var(--crm-danger-bg)', color: 'var(--crm-danger)', border: '1px solid var(--crm-danger)', padding: 10, borderRadius: 6, marginBottom: 12 }}>{error}</div>}
      {!selected ? (
        <table style={{ width: '100%', borderCollapse: 'collapse', background: 'var(--crm-surface)', color: 'var(--crm-text)' }}>
          <thead><tr>
            <th style={th}>Номер</th><th style={th}>Постачальник</th><th style={th}>Очікується</th>
            <th style={th}>Статус</th><th style={{ ...th, textAlign: 'right' }}>Замовлено</th>
            <th style={{ ...th, textAlign: 'right' }}>Отримано</th><th style={{ ...th, textAlign: 'right' }}>Ціна Δ</th><th style={th}>Джерело</th>
          </tr></thead>
          <tbody>
            {orders.map(order => (
              <tr key={order.id} onClick={() => open(order)} style={{ cursor: 'pointer', background: overdueDays(order) > 0 ? 'var(--crm-danger-bg)' : undefined }}>
                <td style={td}><strong>{order.orderNumber}</strong></td>
                <td style={td}>{order.supplierName}</td>
                <td style={td}>{order.expectedDeliveryDate ? new Date(order.expectedDeliveryDate).toLocaleDateString('uk-UA') : '—'}{overdueDays(order) > 0 && <div style={{ color: 'var(--crm-danger)', fontSize: 11 }}>прострочено {overdueDays(order)} дн.</div>}</td>
                <td style={td}>{statusLabels[order.status]}</td>
                <td style={{ ...td, textAlign: 'right' }}>{order.orderedAmount.toFixed(2)} грн</td>
                <td style={{ ...td, textAlign: 'right' }}>{order.receivedAmount.toFixed(2)} грн</td>
                <td style={{ ...td, textAlign: 'right', color: order.priceVariancePercent > 0 ? 'var(--crm-danger)' : 'var(--crm-success)' }}>{order.priceVariancePercent > 0 ? '+' : ''}{order.priceVariancePercent.toFixed(1)}%</td>
                <td style={td}>{order.purchaseWorksheetName}</td>
              </tr>
            ))}
            {orders.length === 0 && <tr><td colSpan={8} style={{ ...td, textAlign: 'center', color: 'var(--crm-text-muted)' }}>Замовлень ще немає</td></tr>}
          </tbody>
        </table>
      ) : (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center', marginBottom: 14 }}>
            <div>
              <h3 style={{ margin: 0 }}>{selected.orderNumber} · {selected.supplierName}</h3>
              <div style={{ color: 'var(--crm-text-muted)', marginTop: 4 }}>
                {statusLabels[selected.status]} · лист «{selected.purchaseWorksheetName}»
                {selected.expectedDeliveryDate ? ` · очікується ${new Date(selected.expectedDeliveryDate).toLocaleDateString('uk-UA')}` : ''}
                {overdueDays(selected) > 0 ? ` · прострочено ${overdueDays(selected)} дн.` : ''}
                {selected.receivedAmount > 0 ? ` · ціна Δ ${selected.priceVariancePercent > 0 ? '+' : ''}${selected.priceVariancePercent.toFixed(1)}%` : ''}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              {selected.status === 'Draft' && <button disabled={busy} style={button('info')} onClick={() => changeStatus('Submitted')}>Відправити</button>}
              {(selected.status === 'Draft' || selected.status === 'Submitted') && <button disabled={busy} style={button('danger')} onClick={() => changeStatus('Cancelled')}>Скасувати</button>}
              <button style={button('muted')} onClick={() => setSelected(null)}>До списку</button>
            </div>
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', background: 'var(--crm-surface)', color: 'var(--crm-text)' }}>
            <thead><tr>
              <th style={th}>Продукт</th><th style={th}>Од.</th><th style={{ ...th, textAlign: 'right' }}>Замовлено</th>
              <th style={{ ...th, textAlign: 'right' }}>Отримано</th><th style={{ ...th, textAlign: 'right' }}>Залишок</th>
              <th style={{ ...th, textAlign: 'right' }}>Ціна план</th><th style={{ ...th, textAlign: 'right' }}>Сума план</th><th style={{ ...th, textAlign: 'right' }}>Факт / Δ</th>
            </tr></thead>
            <tbody>{selected.items.map(item => (
              <tr key={item.id}>
                <td style={td}>{item.productName}</td><td style={td}>{item.weightType}</td>
                <td style={{ ...td, textAlign: 'right' }}>{item.orderedQuantity.toFixed(3)}</td>
                <td style={{ ...td, textAlign: 'right' }}>{item.receivedQuantity.toFixed(3)}</td>
                <td style={{ ...td, textAlign: 'right', color: item.remainingQuantity > 0 ? 'var(--crm-danger)' : 'var(--crm-success)' }}>{item.remainingQuantity.toFixed(3)}</td>
                <td style={{ ...td, textAlign: 'right' }}>{item.unitPrice.toFixed(2)}</td>
                <td style={{ ...td, textAlign: 'right' }}>{item.orderedAmount.toFixed(2)}</td>
                <td style={{ ...td, textAlign: 'right', color: item.priceVariancePercent > 0 ? 'var(--crm-danger)' : 'var(--crm-success)' }}>{item.receivedAmount.toFixed(2)}<div style={{ fontSize: 11 }}>{item.priceVariancePercent > 0 ? '+' : ''}{item.priceVariancePercent.toFixed(1)}%</div></td>
              </tr>
            ))}</tbody>
          </table>
          {(selected.status === 'Submitted' || selected.status === 'PartiallyReceived') && (
            <div style={{ marginTop: 18, border: '1px solid var(--crm-info)', background: 'var(--crm-info-bg)', padding: 14, borderRadius: 8 }}>
              <h4 style={{ marginTop: 0 }}>Прийняти поставку</h4>
              <div className="supplier-purchase-receive-fields" style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
                <input type="date" value={receiptDate} onChange={e => {
                  setReceiptDate(e.target.value);
                  const due = new Date(`${e.target.value}T12:00:00`);
                  due.setDate(due.getDate() + (selected.supplierPaymentTermDays || 0));
                  setPaymentDueDate(due.toISOString().slice(0, 10));
                }} style={fieldStyle} />
                <label>Оплатити до: <input type="date" value={paymentDueDate} onChange={e => setPaymentDueDate(e.target.value)} style={fieldStyle} /></label>
                <input placeholder="Номер накладної" value={invoiceNumber} onChange={e => setInvoiceNumber(e.target.value)} style={fieldStyle} />
                <input placeholder="Примітки" value={notes} onChange={e => setNotes(e.target.value)} style={{ ...fieldStyle, minWidth: 240 }} />
                <label><input type="checkbox" checked={isPaid} onChange={e => setIsPaid(e.target.checked)} /> Оплачено</label>
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse', background: 'var(--crm-surface)', color: 'var(--crm-text)' }}>
                <thead><tr>
                  <th style={th}>Продукт</th><th style={th}>Кількість</th><th style={th}>Фактична ціна</th>
                  <th style={th}>Партія</th><th style={th}>Вироблено</th><th style={th}>Придатний до</th>
                </tr></thead>
                <tbody>{selected.items.filter(item => item.remainingQuantity > 0).map(item => {
                  const row = receiveRows[item.id] || { quantity: '', unitPrice: String(item.unitPrice), lotNumber: '', manufactureDate: '', expirationDate: '' };
                  const patch = (value: Partial<ReceiveRow>) => setReceiveRows(previous => ({ ...previous, [item.id]: { ...row, ...value } }));
                  return <tr key={item.id}>
                    <td style={td}>{item.productName}<div style={{ fontSize: 11, color: 'var(--crm-text-muted)' }}>макс. {item.remainingQuantity.toFixed(3)} {item.weightType}</div></td>
                    <td style={td}><input type="number" min="0" max={item.remainingQuantity} step="0.001" value={row.quantity} onChange={e => patch({ quantity: e.target.value })} style={{ ...fieldStyle, width: 100 }} /></td>
                    <td style={td}><input type="number" min="0" step="0.01" value={row.unitPrice} onChange={e => patch({ unitPrice: e.target.value })} style={{ ...fieldStyle, width: 100 }} /></td>
                    <td style={td}><input value={row.lotNumber} onChange={e => patch({ lotNumber: e.target.value })} style={{ ...fieldStyle, width: 120 }} /></td>
                    <td style={td}><input type="date" value={row.manufactureDate} onChange={e => patch({ manufactureDate: e.target.value })} style={fieldStyle} /></td>
                    <td style={td}><input type="date" value={row.expirationDate} onChange={e => patch({ expirationDate: e.target.value })} style={fieldStyle} /></td>
                  </tr>;
                })}</tbody>
              </table>
              <button disabled={busy} onClick={receive} style={button('success', { marginTop: 10 })}>{busy ? 'Проведення...' : 'Створити прихід'}</button>
            </div>
          )}
          {selected.receiptIds.length > 0 && <div style={{ marginTop: 12, color: 'var(--crm-text-muted)' }}>Створені приходи: {selected.receiptIds.map(id => `#${id}`).join(', ')}</div>}
        </div>
      )}
    </div>
  );
};

export default SupplierPurchaseOrdersPage;
